import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';

import { useFetchEmployerRestaurantsQueryKey } from '../hooks';

import { useV2Router } from '../../dashboard/hooks/use-v2-router';
import { useGettingStartedPage, useRestaurantOnboardingResetActions } from './restaurant-onboarding-modal.hooks';
import useUpdateRestaurantMutation from '../hooks/use-update-restaurant-mutation';
import Image from 'next/image';
import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';
import { showToast, Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

export const newlyCreatedRestaurantIdAtom = atom('');
export const newlyCreatedRestaurantSlugAtom = atom('');

export const stepAfterOnboardingAtom = atom<'check_your_email' | 'customize_showcase' | 'print_qr' | 'social_share'>(
  'check_your_email'
);

export const goToNextStepAfterOnboarding = atom(null, (get, set) => {
  const currentStep = get(stepAfterOnboardingAtom);
  switch (currentStep) {
    case 'check_your_email':
      set(stepAfterOnboardingAtom, 'customize_showcase');
      break;
    case 'customize_showcase':
      set(stepAfterOnboardingAtom, 'print_qr');
      break;
    case 'print_qr':
      set(stepAfterOnboardingAtom, 'social_share');
      break;
    case 'social_share':
      break;
    default:
      break;
  }
});

const goToPreviousStepAfterOnboarding = atom(null, (get, set) => {
  const currentStep = get(stepAfterOnboardingAtom);
  switch (currentStep) {
    case 'customize_showcase':
      set(stepAfterOnboardingAtom, 'check_your_email');
      break;
    case 'print_qr':
      set(stepAfterOnboardingAtom, 'customize_showcase');
      break;
    case 'social_share':
      set(stepAfterOnboardingAtom, 'print_qr');
      break;
    case 'check_your_email':
      break;
    default:
      break;
  }
});

function useStepsContent() {
  const { t } = useTranslation('restaurant-onboarding');
  return {
    check_your_email: {
      title: t('next_steps.check_your_email.title'),
      description: t('next_steps.check_your_email.description'),
      icon: (
        <Image
          src={getAssetSrc('/utils-assets/check_your_email.png')}
          alt="Check your email"
          width={124}
          height={124}
          loader={customImageLoader}
          className="object-contain"
        />
      ),
    },
    customize_showcase: {
      title: t('next_steps.customize_showcase.title'),
      description: t('next_steps.customize_showcase.description'),
      icon: <EmojiIcon>🖌️</EmojiIcon>,
    },
    print_qr: {
      title: t('next_steps.print_qr.title'),
      description: t('next_steps.print_qr.description'),
      icon: (
        <Image
          src={getAssetSrc('/utils-assets/print_qr.png')}
          alt="Print your QR code"
          width={96}
          height={96}
          loader={customImageLoader}
          className="object-contain"
        />
      ),
    },
    social_share: {
      title: t('next_steps.social_share.title'),
      description: t('next_steps.social_share.description'),
      icon: <EmojiIcon>🔗</EmojiIcon>,
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IRestaurantOnboardingSuccessfulProps {}

// Wrapper con animazione fade + slide quando cambia lo step
const StepTransitionContainer = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('animate-[fadeSlideIn_420ms_cubic-bezier(0.16,1,0.3,1)_forwards] opacity-0', className)}
    style={{
      ...style,
    }}
    {...props}
  />
);

const EmojiIcon = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-[58px] leading-none', className)} {...props} />
);

const RestaurantOnboardingSuccessful: React.FunctionComponent<IRestaurantOnboardingSuccessfulProps> = (props) => {
  const queryClient = useQueryClient();
  const { handleModalClose } = useRestaurantOnboardingResetActions();
  const { t } = useTranslation('restaurant-onboarding');
  const { t: tC } = useTranslation('common');
  const refetchRestaurantsQueryKey = useFetchEmployerRestaurantsQueryKey();
  const newlyCreatedRestaurantSlug = useAtomValue(newlyCreatedRestaurantSlugAtom);
  const router = useV2Router();
  const isGettingStartedPage = useGettingStartedPage();
  const { reset } = useUpdateRestaurantMutation();
  const currentStep = useAtomValue(stepAfterOnboardingAtom);
  const goToNextStep = useSetAtom(goToNextStepAfterOnboarding);
  const goToPreviousStep = useSetAtom(goToPreviousStepAfterOnboarding);
  const stepContent = useStepsContent();

  const onFinish = React.useCallback(() => {
    if (isGettingStartedPage) router.push(`/employer/venues/${newlyCreatedRestaurantSlug}`);
    else {
      handleModalClose();
      queryClient.invalidateQueries({ queryKey: refetchRestaurantsQueryKey });
    }
    reset();
  }, [
    handleModalClose,
    isGettingStartedPage,
    queryClient,
    reset,
    refetchRestaurantsQueryKey,
    router,
    newlyCreatedRestaurantSlug,
  ]);

  React.useEffect(() => {
    showToast(t('onboarding_successful'), 'success', { autoClose: 5000, position: 'top-right' });
  }, [t]);

  return (
    <div className="bg-purple-50 w-full h-full flex flex-row justify-center">
      <div className="flex flex-col justify-between items-center w-full md:max-w-[640px] pb-4 p-4">
        <div className="w-full flex flex-row">
          {currentStep !== 'check_your_email' && (
            <ButtonV2
              variant="tertiary"
              StartIcon={ChevronLeftIcon}
              className="shadow-none"
              onClick={(e) => {
                e.preventDefault();
                goToPreviousStep();
              }}
            >
              <Heading>{tC('back')}</Heading>
            </ButtonV2>
          )}
        </div>
        <StepTransitionContainer
          key={currentStep}
          className="w-full self-stretch h-full px-4 py-10 inline-flex flex-col justify-center items-center gap-6 overflow-hidden text-center"
        >
          <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center">
            {stepContent[currentStep].icon}
          </div>
          <h2
            className="self-stretch text-center justify-start text-purple-700 text-2xl font-semibold"
            dangerouslySetInnerHTML={{ __html: stepContent[currentStep].title }}
          />
          <p dangerouslySetInnerHTML={{ __html: stepContent[currentStep].description }} />
        </StepTransitionContainer>
        <div className="w-full flex flex-col gap-6">
          <ProgressDots />
          <ButtonV2
            variant="secondary-restworld"
            onClick={(e) => {
              e.preventDefault();
              if (currentStep === 'social_share') onFinish();
              else goToNextStep();
            }}
            className="w-full"
          >
            {t('continue')}
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};

const ProgressDots: React.FunctionComponent = () => {
  const currentStep = useAtomValue(stepAfterOnboardingAtom);
  const steps = ['check_your_email', 'customize_showcase', 'print_qr', 'social_share'];
  const currentStepIndex = steps.indexOf(currentStep);

  return (
    <div className="flex flex-row justify-center items-center gap-3">
      {steps.map((step, index) => (
        <Dot key={step} $isActive={index === currentStepIndex} />
      ))}
    </div>
  );
};

const Dot = ({ $isActive, className, ...props }: { $isActive: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'w-4 h-4 rounded-full border border-purple-700 bg-transparent',
      $isActive ? 'bg-purple-700' : '',
      className
    )}
    {...props}
  />
);

export default RestaurantOnboardingSuccessful;
